import React, { useState } from 'react';
import { useStore } from '../context/StoreContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import DocumentGeneratorModal from '../components/DocumentGeneratorModal';
import TemplateModal from '../components/TemplateModal';

const { FiPlus, FiFileText, FiLayers, FiZap, FiEdit2, FiTrash2, FiSearch, FiFilter, FiActivity } = FiIcons;

const DocumentTemplates = () => {
  const { data, addRecord, updateRecord, deleteRecord } = useStore();
  const [isGeneratorOpen, setIsGeneratorOpen] = useState(false);
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  const templates = (data.documentTemplates || []).filter(t => 
    t.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
    t.category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const [filterCategory, setFilterCategory] = useState('All');
  const categories = ['All', 'General', 'Onboarding', 'Lease', 'Maintenance', 'Financial', 'Legal', 'Hospitality', 'Notice'];
  const DOC_TYPE_ICONS = { email: '✉️', lease: '📋', letter: '📄', 'move-in': '🏠', 'move-out': '📦', inspection: '🔍', receipt: '🧾', other: '📝' };

  const displayTemplates = filterCategory === 'All' ? templates : templates.filter(t => t.category === filterCategory);

  const handleCreateTemplate = () => {
    setEditingTemplate(null);
    setIsEditorOpen(true);
  };

  const handleEditTemplate = (template) => {
    setEditingTemplate(template);
    setIsEditorOpen(true);
  };

  const handleGenerateDocument = (doc) => {
    addRecord('documents', doc);
    // Logic to show success toast could go here
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-500 pb-12">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-black text-gray-800 tracking-tight">Dynamic Blueprints</h2>
          <p className="text-sm text-gray-500 font-medium italic">Transforming data into professional midterm documents.</p>
        </div>
        <div className="flex gap-2">
          <button 
            onClick={handleCreateTemplate}
            className="flex items-center px-5 py-3 bg-white border border-gray-200 text-gray-700 rounded-2xl hover:bg-gray-50 transition shadow-sm font-black text-sm uppercase tracking-wider"
          >
            <SafeIcon icon={FiPlus} className="mr-2" /> Design Template
          </button>
          <button 
            onClick={() => setIsGeneratorOpen(true)}
            className="flex items-center px-5 py-3 bg-indigo-600 text-white rounded-2xl hover:bg-indigo-700 transition shadow-lg shadow-indigo-100 font-black text-sm uppercase tracking-wider"
          >
            <SafeIcon icon={FiZap} className="mr-2" /> Generate Document
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm overflow-hidden relative group">
          <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">Total Templates</p>
          <p className="text-3xl font-black text-gray-900">{templates.length}</p>
          <SafeIcon icon={FiLayers} className="absolute -bottom-2 -right-2 text-indigo-50 opacity-0 group-hover:opacity-100 transition-opacity" size={60} />
        </div>
        <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm overflow-hidden relative group">
          <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">Documents Generated</p>
          <p className="text-3xl font-black text-indigo-600">{data.documents?.length || 0}</p>
          <SafeIcon icon={FiActivity} className="absolute -bottom-2 -right-2 text-indigo-50 opacity-0 group-hover:opacity-100 transition-opacity" size={60} />
        </div>
        <div className="md:col-span-2 bg-indigo-600 p-6 rounded-3xl text-white shadow-lg flex items-center justify-between">
          <div>
            <h3 className="font-black text-lg">Auto-Fill Intelligence</h3>
            <p className="text-xs text-indigo-100 font-medium">22+ merge fields auto-fill from tenant, property & lease data.</p>
          </div>
          <div className="flex -space-x-2">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="w-8 h-8 rounded-full border-2 border-indigo-600 bg-indigo-400 flex items-center justify-center text-[10px] font-black">
                {String.fromCharCode(64 + i)}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="flex flex-col md:flex-row gap-4 bg-white p-4 rounded-3xl shadow-sm border border-gray-100">
        <div className="relative flex-1">
          <SafeIcon icon={FiSearch} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
          <input 
            type="text" 
            placeholder="Search templates by name or category..." 
            className="w-full pl-12 pr-4 py-3 bg-gray-50 border-none rounded-2xl outline-none focus:ring-2 focus:ring-indigo-500 text-sm font-medium"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="flex items-center gap-2">
          <SafeIcon icon={FiFilter} className="text-gray-400" />
          <select className="bg-gray-50 border-none rounded-2xl px-4 py-3 text-sm font-bold text-gray-600 outline-none"
            value={filterCategory} onChange={e => setFilterCategory(e.target.value)}>
            {categories.map(c => <option key={c} value={c}>{c === 'All' ? 'All Categories' : c}</option>)}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {displayTemplates.map(t => (
          <div key={t.id} className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm relative group hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
            <div className="flex justify-between items-start mb-6">
              <div className="flex items-center gap-3">
                <span className="text-3xl">{DOC_TYPE_ICONS[t.docType] || '📝'}</span>
                <div>
                  <span className="text-[9px] font-bold text-gray-400 uppercase tracking-widest">{(t.docType || 'other').replace('-', ' ')}</span>
                </div>
              </div>
              <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                <button 
                  onClick={() => handleEditTemplate(t)}
                  className="p-2 text-gray-400 hover:text-indigo-600 transition bg-gray-50 rounded-xl"
                >
                  <SafeIcon icon={FiEdit2} size={16} />
                </button>
                <button 
                  onClick={() => deleteRecord('documentTemplates', t.id)}
                  className="p-2 text-gray-400 hover:text-red-600 transition bg-gray-50 rounded-xl"
                >
                  <SafeIcon icon={FiTrash2} size={16} />
                </button>
              </div>
            </div>

            <div>
              <h4 className="text-lg font-black text-gray-900 mb-1 leading-tight">{t.name || t.title}</h4>
              <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-4">{t.category}</p>
              
              <div className="bg-gray-50 rounded-2xl p-4 min-h-[100px] border border-gray-50">
                <p className="text-xs text-gray-600 font-medium line-clamp-4 italic leading-relaxed">
                  {t.content}
                </p>
              </div>
              
              <div className="mt-6 flex items-center justify-between">
                <span className="text-[9px] font-black text-gray-400 uppercase">Updated {t.lastUpdated}</span>
                <button 
                  onClick={() => { setEditingTemplate(t); setIsGeneratorOpen(true); }}
                  className="text-[10px] font-black text-indigo-600 uppercase tracking-widest flex items-center gap-1 hover:underline"
                >
                  <SafeIcon icon={FiZap} size={10} /> Use Now
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <DocumentGeneratorModal
        isOpen={isGeneratorOpen}
        onClose={() => setIsGeneratorOpen(false)}
        templates={data.documentTemplates}
        properties={data.properties}
        leases={data.leases}
        tenants={data.tenants}
        onGenerate={handleGenerateDocument}
        customFieldDefs={data.customFieldDefs || []}
      />

      <TemplateModal
        isOpen={isEditorOpen}
        onClose={() => setIsEditorOpen(false)}
        onSave={(t) => editingTemplate ? updateRecord('documentTemplates', editingTemplate.id, t) : addRecord('documentTemplates', t)}
        initialData={editingTemplate}
        customFieldDefs={data.customFieldDefs || []}
        onAddCustomField={(field) => addRecord('customFieldDefs', field)}
        onRemoveCustomField={(fieldName) => {
          const def = (data.customFieldDefs || []).find(f => f.name === fieldName);
          if (def) deleteRecord('customFieldDefs', def.id);
        }}
      />
    </div>
  );
};

export default DocumentTemplates;